import React, { useContext, useEffect, useState, useRef } from 'react';
import { Button, useFlashMessage } from '@indigo-ag/component-module';
import { CreditApprovalRound, UpdateCreditApprovalRoundExceptionsInput } from '@indigo-ag/schema';
import { useMutation } from '@apollo/client';
import i18next from 'core/i18n';

import { utils } from 'shared';
import {
  getCreditApprovalData,
  updateCreditApprovalRoundExceptions
} from 'features/brazil-grower/queries/creditApproval.queries';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import CreditExceptionsForm, { CreditExceptionsFormHandle } from '../CreditExceptions/CreditExceptionsForm';
import { ActionsWrapper, ExceptionButton, Modal } from './EditCreditExceptions.styles';

interface EditCreditExceptionsProps {
  round?: CreditApprovalRound;
  alreadyAddedExceptions?: string[];
}

export const EditCreditExceptions = ({ round, alreadyAddedExceptions }: EditCreditExceptionsProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newExceptions, setNewExceptions] = useState<string[]>(round?.creditExceptions ?? []);
  const [inputValue, setInputValue] = useState('');
  const formRef = useRef<CreditExceptionsFormHandle>(null);

  const initialExceptions = round?.creditExceptions ?? [];

  useEffect(() => {
    setNewExceptions(round?.creditExceptions ?? []);
  }, [round, isModalOpen]);

  const [updateCreditApprovalRoundExceptionsFn, { loading }] = useMutation(updateCreditApprovalRoundExceptions);

  const { agFinancingId } = useContext(FinanceProcessContext);

  const { addFlashMessage } = useFlashMessage();

  const openCreditExceptionsModal = () => setIsModalOpen(true);

  const closeCreditExceptionsModal = () => {
    setIsModalOpen(false);
    setInputValue('');
  };

  // Round completed
  if (utils.CreditApprovalUtil.isRoundComplete(round) || !round) {
    return null;
  }

  const updateCreditExceptions = () => {
    const newExceptionsCopy = newExceptions.slice();
    const haveExceptionsChanged =
      JSON.stringify(initialExceptions.slice().sort()) !== JSON.stringify(newExceptionsCopy.sort());

    if (!(haveExceptionsChanged && round)) {
      closeCreditExceptionsModal();
      return Promise.resolve();
    }

    if (formRef.current?.hasOtherExceptionSelected() && !formRef.current?.validateOtherException(inputValue)) {
      return;
    }

    const input: UpdateCreditApprovalRoundExceptionsInput = {
      selectedExceptions: newExceptions
    };

    return updateCreditApprovalRoundExceptionsFn({
      variables: {
        creditApprovalRoundId: round.id,
        creditApprovalRoundExceptionsInput: input
      },
      refetchQueries: [
        {
          query: getCreditApprovalData,
          variables: { agFinancingId }
        }
      ],
      awaitRefetchQueries: true
    })
      .then(() => {
        addFlashMessage({
          content: '',
          title: i18next.t('creditExceptionsSuccessfullyUpdated'),
          variant: 'success'
        });
      })
      .catch(() => {
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        });
      })
      .finally(() => {
        closeCreditExceptionsModal();
      });
  };

  const modalContent = () => (
    <CreditExceptionsForm
      ref={formRef}
      newExceptions={newExceptions}
      setNewExceptions={setNewExceptions}
      inputValue={inputValue}
      setInputValue={setInputValue}
      alreadyAdded={alreadyAddedExceptions}
    />
  );

  const modalActions = () => (
    <ActionsWrapper>
      <Button onClick={() => closeCreditExceptionsModal()} type="button" size="small" variant="secondary">
        {i18next.t('cancel')}
      </Button>
      <Button onClick={updateCreditExceptions} type="submit" size="small" busy={loading}>
        {i18next.t('save')}
      </Button>
    </ActionsWrapper>
  );

  return (
    <>
      <Modal
        open={isModalOpen}
        onRequestClose={closeCreditExceptionsModal}
        title={i18next.t('editExceptions')}
        renderContent={modalContent}
        renderActionArea={modalActions}
      />
      <ExceptionButton onClick={() => openCreditExceptionsModal()}>{i18next.t('editExceptions')}</ExceptionButton>
    </>
  );
};
